import { FC, ReactNode, useEffect, useMemo } from 'react'
import styled from 'styled-components'

import { ViewportSizes, useIsBrowser, useIsViewport } from '@syconium/little-miss-figgy'

import { useFeatureFlag } from '../../../../../../app/_providers/ExperimentationProvider.client'
import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { MultiAddToBagService } from '../../../../../../brunswick/containers/multi-add-to-bag'
import { RefsContainer } from '../../../../../../brunswick/containers/refs'
import { CtaButton } from '../../../../../../components/atoms/CtaButton'
import { KitEmbroideryService } from '../../../../../../containers/EmbroideryService'
import { trackEvent } from '../../../../../../lib/analytics'
import { useBagMultiple } from '../../../../../../lib/hooks/useBagMultiple'
import { Variant } from '../../../../../../types/graphql'
import { StickyAddToBagModal } from '../../../ProductPage/DetailsSection/StickyAddToBagModal'

import { Container } from './styles'

const STICKY_CART_VIEWPORTS = [ViewportSizes.small]

export interface KitAddToBagProps
  extends Readonly<{
    discountPrice?: number | null
    hasItemWithMissingSize: boolean
    icon?: ReactNode
    isKitFinalSale: boolean
    kitName: string
    kitSku: string
    price: number
    swPromoEligible: boolean | undefined
    variants: ReadonlyArray<Variant> | null
  }> {}

interface AddToBagButtonContentWrapperProps {
  isIconVisible?: boolean
}

const AddToBagButtonContentWrapper = styled.div<AddToBagButtonContentWrapperProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${o => o.theme.spacing(4)};
  ${o => (o.isIconVisible ? `margin-right: ${o.theme.spacing(4)};` : '')}
`

export const KitAddToBag: FC<KitAddToBagProps> = ({
  discountPrice,
  hasItemWithMissingSize,
  isKitFinalSale,
  kitName,
  kitSku,
  price,
  icon,
  swPromoEligible,
  variants,
}) => {
  const isBrowser = useIsBrowser()
  const isValidStickyCartViewport = useIsViewport(STICKY_CART_VIEWPORTS)
  const {
    detailsSection: { addToBagCTA, inFlightAddToBagCTA, selectSizeKitsAddToBagButtonCTA },
  } = useTranslation()
  const { detailsSectionAddToBagSectionRef } = RefsContainer.useContainer()

  const kitEmbroideryService = KitEmbroideryService.useContainer()

  //need an id that includes variant and selected embroidery details. For example if you add a kit to the cart
  //then change some color options and add the same kit this should be grouped as a
  //separate item in the mini cart instead of incrementing the quantity of the previously added kit
  const embroideryId = kitEmbroideryService.getEmbroideryId()
  const kitId =
    variants
      ?.map(o => o?.sku)
      .sort()
      .concat(embroideryId)
      .join() || ''

  const { handleAddToBagClick, resetInvalidBagClickStatus } = useBagMultiple({
    hasItemWithMissingSize,
    kitName,
    kitSku,
    kitId,
    variants,
    isKitFinalSale,
    discountPrice,
    price,
    swPromoEligible,
  })

  const addToBagCtaCopyTestDecision = useFeatureFlag<
    'atb-cta-copy-test-control' | 'atb-cta-copy-test-variant'
  >({
    key: 'atb-cta-copy-test',
    controlVariant: 'atb-cta-copy-test-control',
    ifAccessedPriorToDecisionInitialization: 'lock-pageview-to-controlVariant',
  })

  const isSelectSizeCtaCopyVariant = addToBagCtaCopyTestDecision === 'atb-cta-copy-test-variant'
  const shouldShowSelectSizeCta = isSelectSizeCtaCopyVariant && hasItemWithMissingSize

  const { addToBagErrors, isAddToCartInFlight } = MultiAddToBagService.useContainer()

  // TODO: Fix this auto-generated suppression.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(resetInvalidBagClickStatus, [variants])
  // This is a bit tricky.  We had an issue where on desktop only, arriving at
  // a PDP with no size specified led to a situation in which the add to bag button
  // presented as disabled immediately and would not enable upon size selection
  // despite being functionally correct.

  // When react reconciles the dom during hydration, it WILL NOT replace mismatched
  // properties, although it WILL reconcile mismatched text nodes.

  // const Home: React.FC = () => {
  //   const isServer: boolean = typeof window === "undefined"
  //   return (
  //     <main>
  //       <h1>Props Mismatch</h1>
  //       <p style={{ color: isServer ? "red" : "green" }}>Is Server: {isServer}</p>
  //     </main>
  //   );
  // };

  // This results in p[style] being stuck with color red on the client while the
  // Is Server: text eventually renders the correct false.
  //
  // The react state at this point is correct, but will never be reconciled with the
  // dom until some other state change leads to an update of p
  //
  // The takeaway here is in cases where device detection or anything else which is
  // unknowable on the server side is implicated in an expression used to generate
  // styled components, or anything other than data eventually ending up in a text node,
  // at time of first render the data must match, even if it is possible to know
  // the initial value is not correct as with useIsViewport here.
  //
  // Correcting the state must be deferred to useEffect.
  //
  // Thank you to Kyle for the minimal repros and assist in troubleshooting this one.
  //
  // https://github.com/facebook/react/issues/11128

  useEffect(() => {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddToCartInFlight, hasItemWithMissingSize, addToBagErrors.missingRequiredSize])

  const addToBagButtonCTA: string = useMemo(() => {
    if (isAddToCartInFlight) return inFlightAddToBagCTA
    if (shouldShowSelectSizeCta) return selectSizeKitsAddToBagButtonCTA

    return addToBagCTA
  }, [
    addToBagCTA,
    inFlightAddToBagCTA,
    isAddToCartInFlight,
    selectSizeKitsAddToBagButtonCTA,
    shouldShowSelectSizeCta,
  ])

  const isIconVisible = !!icon && !isAddToCartInFlight && !shouldShowSelectSizeCta

  const addToBagButton: JSX.Element = (
    <CtaButton
      allCaps
      block
      data-testid='add-to-bag-button'
      isProcessing={isAddToCartInFlight}
      onClick={handleAddToBagClick}
      {...trackEvent({
        category: 'kit details',
        action: 'add to cart',
        label: addToBagButtonCTA,
      })}
    >
      <AddToBagButtonContentWrapper isIconVisible={isIconVisible}>
        {isIconVisible && icon}
        <span aria-live='polite'>{addToBagButtonCTA}</span>
      </AddToBagButtonContentWrapper>
    </CtaButton>
  )

  return (
    <>
      <Container ref={detailsSectionAddToBagSectionRef}>{addToBagButton}</Container>
      {isValidStickyCartViewport && isBrowser ? (
        <StickyAddToBagModal>{addToBagButton}</StickyAddToBagModal>
      ) : null}
    </>
  )
}
